import { useMutation } from '@apollo/client';
import { Actions, Button, FlexGrid, Header, Image, Modal, Panel, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useProfile from '../../../lib/hooks/useProfile';
import { toShortLocaleDate } from '../../../lib/utils/formatDateTime';
import { ANALYTICS_EVENTS, DEFAULT_LOGO_URL } from '../../constants';
import { options } from '../../constants/reviewRatings';
import styles from './ReviewItem.module.css';

const cx = classnames.bind(styles);

export const OPINE_REVIEW = gql`
  mutation OpineReview($isAgreeing: Boolean!, $reviewId: ID!) {
    opineReview(isAgreeing: $isAgreeing, reviewId: $reviewId)
  }
`;

export const FLAG_REVIEW = gql`
  mutation FlagReview($recaptchaResponse: String!, $reviewId: ID!) {
    flagReview(recaptchaResponse: $recaptchaResponse, reviewId: $reviewId)
  }
`;

export default function ReviewItem({
  interactive,
  review,
  showReplies,
}) {
  const isAnonUser = !useProfile().isLoggedIn;
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [recaptchaAttempted, setRecaptchaAttempted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState();
  const analytics = useAnalytics();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t, i18n } = useTranslation(['review', 'account', 'miscellaneous']);

  const [opineReview, { error: opineError, loading: isOpining }] = useMutation(OPINE_REVIEW, {
    update(cache, { data }) {
      // update cache with new isReportable and hasAgreed values
      cache.modify({
        id: cache.identify(review),
        fields: {
          agreeTotal(oldAgreeTotal) {
            return oldAgreeTotal + (data.opineReview ? 1 : -1);
          },
          hasAgreed() {
            return data.opineReview;
          },
          isReportable() {
            return !data.opineReview;
          },
        },
      });
    },
  });

  const [flagReview, { called: flagCompleted, error: flagError, loading: isFlagging }] = useMutation(FLAG_REVIEW, {
    update(cache, { data }) {
      // update cache with new isReportable value
      cache.modify({
        id: cache.identify(review),
        fields: {
          isReportable() {
            return data.flagReview;
          },
        },
      });

      analytics.tagEvent({ name: ANALYTICS_EVENTS.REVIEW_FLAGGED });
    },
  });

  const redirectAnonUser = useCallback(() => {
    window.location.href = '/login';
  }, []);


  const handleAgreeWithReview = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.REVIEW_AGREED,
      attributes: { isLoggedIn: !isAnonUser },
    });

    if (isAnonUser) {
      redirectAnonUser();
      return;
    }

    opineReview({
      variables: {
        isAgreeing: true,
        reviewId: review.reviewId,
      },
    });
  }, [analytics, isAnonUser, opineReview, review.reviewId, redirectAnonUser]);

  const handleUnagreeWithReview = useCallback(() => {
    // since only logged in users can unagree review, adding context whether user is logged in or not isn't needed
    analytics.tagEvent({ name: ANALYTICS_EVENTS.REVIEW_UNAGREED });

    opineReview({
      variables: {
        isAgreeing: false,
        reviewId: review.reviewId,
      },
    });
  }, [analytics, opineReview, review.reviewId]);

  const handleCancelFlagReview = useCallback(() => {
    setShowFlagModal(false);
    setRecaptchaToken(undefined);
    setRecaptchaAttempted(false);
  }, []);

  const handleFlagReview = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.REVIEW_FLAG_CLICKED,
      attributes: { isLoggedIn: !isAnonUser },
    });

    if (isAnonUser) {
      redirectAnonUser();
      return;
    }
    setShowFlagModal(true);
  }, [analytics, isAnonUser, redirectAnonUser]);

  const handleConfirmFlagReview = useCallback(async () => {
    const responseToken = await executeRecaptcha('flagReview');
    setRecaptchaToken(responseToken);
    setRecaptchaAttempted(true);
    if (responseToken) {
      flagReview({
        variables: {
          recaptchaResponse: responseToken,
          reviewId: review.reviewId,
        },
      });
    }
  }, [executeRecaptcha, flagReview, review.reviewId]);

  const {
    agreeTotal,
    hasAgreed,
    isReportable,
    memberId,
    overallRating,
    profileImageUrl,
    replies,
    review: reviewContent,
    reviewDate,
  } = review;

  const reviewRating = overallRating < 1 ? 1 : overallRating; // safe non 0 ratings
  const formattedReviewDate = toShortLocaleDate(reviewDate, i18n.language);
  const reviewRatingOption = options.find(option => option.value === reviewRating);
  const intAgreeTotal = Number(agreeTotal) || 0; // ensure agreeTotal is a number

  const updateError = opineError || flagError;
  const isUpdating = isOpining || isFlagging;

  return (
    <Panel bordered className={cx('reviewItemPanel')} color="white" rounded>
      {showFlagModal && (
        <Modal
          size="sm"
          content={() => (
            flagCompleted
              ? (
                <Fragment>
                  <Header as="h3">
                    {t('feedbackHeader', { ns: 'review' })}
                  </Header>
                  <Text as="p">
                    {t('feedbackMessage', { ns: 'review' })}
                  </Text>
                  <br />
                  <Button primary wide onClick={handleCancelFlagReview}>
                    {t('actions.done', { ns: 'miscellaneous' })}
                  </Button>
                </Fragment>
              )
              : (
                <Fragment>
                  <Header as="h3">
                    {t('flagReviewHeader', { ns: 'review' })}
                  </Header>
                  <Text as="p">
                    {t('inappropriateMessage', { ns: 'review' })}
                  </Text>
                  <Text as="p">
                    <Trans i18nKey="review:onceFlaggedMessage">
                      Once you flag a review, you will be unable to agree with it and we will verify that you are not a robot <span role="img" aria-labelledby="recaptcha">🤖</span>
                    </Trans>
                  </Text>
                  <br /><br />
                  {recaptchaAttempted && !recaptchaToken && (
                    <Text as="p" color="orange">
                      {t('flagVerificationFailed', { ns: 'review' })}
                    </Text>
                  )}
                  {!!updateError && (
                    <Text as="p" color="orange">
                      {t('updateReviewError', { ns: 'review' })}
                    </Text>
                  )}
                  <Actions>
                    <Actions.Button
                      primary
                      fluid
                      onClick={handleConfirmFlagReview}
                      loading={isUpdating}
                      disabled={!executeRecaptcha}
                      desktop={5}
                    >
                      {t('actions.yesImSure', { ns: 'miscellaneous' })}
                    </Actions.Button>
                    <Actions.Button
                      secondary
                      fluid
                      onClick={handleCancelFlagReview}
                      desktop={5}
                    >
                      {t('actions.cancel', { ns: 'miscellaneous' })}
                    </Actions.Button>
                  </Actions>
                </Fragment>
              )
          )}
          forceIsShowing={showFlagModal}
          onClose={handleCancelFlagReview}
        />
      )}
      <FlexGrid>
        <FlexGrid.Column mobile={8} tablet={10} className={cx('headerContainer')}>
          <div className={cx('inlineContainer')}>
            <Image
              alt={t('avatarImage', { ns: 'account', memberId })}
              circular
              className={cx('iconSizing')}
              src={profileImageUrl}
            />
          </div>
          <div className={cx('titleContainer')}>
            <Text as="a" color="midnight" href={`https://www.gasbuddy.com/member/${memberId.toLowerCase()}`}>
              <Text bold color="midnight">{memberId}</Text>
            </Text>
            <br />
            <Text className={cx('date')}>{formattedReviewDate}</Text>
          </div>
        </FlexGrid.Column>
        <FlexGrid.Column mobile={4} tablet={2} className={cx('ratingContainer')}>
          <Image
            alt={t('reviewIcon', { ns: 'review', label: reviewRatingOption.label })}
            className={cx('iconSizing')}
            src={reviewRatingOption.icon}
          />
        </FlexGrid.Column>
        <FlexGrid.Column>
          <Text as="p" className={cx('content')}>{reviewContent}</Text>
        </FlexGrid.Column>
        {interactive && (
          <Fragment>
            <FlexGrid.Column mobile={7}>
              {(isAnonUser || isReportable) && (
                <Button link className={cx('flagButton')} onClick={handleFlagReview}>
                  {t('flagReview', { ns: 'review' })}
                </Button>
              )}
              {!isAnonUser && !hasAgreed && !isReportable && (
                <Text className={cx('flagged')}>
                  {t('flaggedReview', { ns: 'review' })}
                </Text>
              )}
            </FlexGrid.Column>
            <FlexGrid.Column mobile={5} className={cx('agreeButtonContainer')}>
              {(isAnonUser || hasAgreed || isReportable) && (
                <Button
                  className={cx('agreeButton', { agreed: hasAgreed })}
                  loading={isUpdating}
                  onClick={hasAgreed ? handleUnagreeWithReview : handleAgreeWithReview}
                  size="sm"
                >
                  <Image
                    alt={t('agreeIcon', { ns: 'review' })}
                    className={cx('agreeIcon')}
                    src={hasAgreed ? '//static.gasbuddy.com/web/icons/thumbsup-active.svg' : '//static.gasbuddy.com/web/icons/thumbsup.svg'}
                  />
                  &nbsp;&nbsp;
                  {t('countAgree', { ns: 'review', count: intAgreeTotal })}
                </Button>
              )}
            </FlexGrid.Column>
            {!!updateError && (
              <FlexGrid.Column>
                <Text as="p" color="orange">
                  {t('updateReviewError', { ns: 'review' })}
                </Text>
              </FlexGrid.Column>
            )}
          </Fragment>
        )}
        {showReplies && replies.length > 0 && replies.map((reply) => {
          const formattedReplyDate = toShortLocaleDate(reply.createDate, i18n.language);
          return (
            <FlexGrid.Column mobile={10} mobileOffset={2} key={reply.id}>
              <div className={cx('replyContainer')}>
                <div className={cx('headerContainer')}>
                  <Image
                    src={(reply.brand?.imageUrl) || DEFAULT_LOGO_URL}
                    className={cx('replyIcon')}
                    alt={t('corporateLogo', { ns: 'review', brandName: reply.brand?.name || '' })}
                    circular
                    bordered
                  />
                  <div className={cx('titleContainer')}>
                    <Text bold color="midnight" className={cx('replyIcon')}>
                      {t('replyFrom', { ns: 'review', authorName: reply.authorName })}
                    </Text>
                    <br />
                    <Text className={cx('date')}>{formattedReplyDate}</Text>
                  </div>
                </div>
                <Text as="p" className={cx('content')}>{reply.text}</Text>
              </div>
            </FlexGrid.Column>
          );
        })}
      </FlexGrid>
    </Panel>
  );
}

ReviewItem.propTypes = {
  interactive: PropTypes.bool,
  review: PropTypes.shape({
    agreeTotal: PropTypes.number,
    hasAgreed: PropTypes.bool,
    isReportable: PropTypes.bool,
    memberId: PropTypes.string.isRequired,
    overallRating: PropTypes.number,
    profileImageUrl: PropTypes.string,
    replies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        createDate: PropTypes.string,
        brandId: PropTypes.number,
        authorName: PropTypes.string,
        text: PropTypes.string,
      }),
    ),
    review: PropTypes.string,
    reviewId: PropTypes.string,
    reviewDate: PropTypes.string,
  }).isRequired,
  showReplies: PropTypes.bool,
};

ReviewItem.defaultProps = {
  interactive: false,
  showReplies: false,
};
